// SNAP-FC-BUILD: 2026-07-31-track-padding-v6
if (!customElements.get('featured-collection-snap')) {
  customElements.define(
    'featured-collection-snap',
    class FeaturedCollectionSnap extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.blockId = this.dataset.blockId;
        this.block = this.closest(`[data-id="${this.blockId}"]`);
        this.track = this.querySelector('.fc-snap__track');

        if (!this.block || !this.track) return;

        this.slides = Array.from(this.track.children).filter((element) =>
          element.classList.contains('fc-snap__slide')
        );
        this.previousButton = this.block.querySelector('.swiper-button-prev');
        this.nextButton = this.block.querySelector('.swiper-button-next');
        this.pagination = this.block.querySelector('.fc-snap__pagination');

        this.snapDesktop = this.dataset.snapDesktop === 'true';
        this.snapMobile = this.dataset.snapMobile === 'true';
        this.contentOverflow = this.dataset.contentOverflow === 'true';
        this.desktopMedia = window.matchMedia('(min-width: 768px)');

        this.hasEnteredViewport = false;
        this.sectionObserver = null;
        this.slideObserver = null;
        this.scrollFrame = null;
        this.dragState = null;
        this.suppressClick = false;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleModeChange = this.handleModeChange.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerEnd = this.handlePointerEnd.bind(this);
        this.handleDragStart = this.handleDragStart.bind(this);
        this.handleTrackClick = this.handleTrackClick.bind(this);
        this.handleQuickViewRequest = this.handleQuickViewRequest.bind(this);
        this.handleMediaLoad = this.handleMediaLoad.bind(this);

        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.track.addEventListener('keydown', this.handleKeydown);
        this.track.addEventListener('pointerdown', this.handlePointerDown);
        this.track.addEventListener('pointermove', this.handlePointerMove);
        this.track.addEventListener('pointerup', this.handlePointerEnd);
        this.track.addEventListener('pointercancel', this.handlePointerEnd);
        this.track.addEventListener('dragstart', this.handleDragStart);
        this.track.addEventListener('click', this.handleTrackClick, true);
        this.addEventListener('pointerdown', this.handleQuickViewRequest, true);
        this.addEventListener('click', this.handleQuickViewRequest, true);

        if (this.previousButton) {
          this.previousButton.addEventListener('click', this.handlePrevious);
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.handleNext);
        }

        if (typeof this.desktopMedia.addEventListener === 'function') {
          this.desktopMedia.addEventListener('change', this.handleModeChange);
        } else {
          this.desktopMedia.addListener(this.handleModeChange);
        }

        this.handleViewportResize = () => {
          this.updateViewportBleed();
          this.updateControls();
          this.calcNavButtonsPosition();
        };
        window.addEventListener('resize', this.handleViewportResize);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.handleViewportResize);
          this.resizeObserver.observe(this);
        }

        this.updateViewportBleed();
        this.updateControls();
        this.calcNavButtonsPosition();

        if (window.Shopify && window.Shopify.designMode) {
          this.hasEnteredViewport = true;
          this.observeSlides();
        } else {
          this.observeVisibility();
        }
      }

      disconnectedCallback() {
        if (this.sectionObserver) {
          this.sectionObserver.disconnect();
          this.sectionObserver = null;
        }

        this.disconnectSlideObserver();

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }

        if (this.handleViewportResize) {
          window.removeEventListener('resize', this.handleViewportResize);
        }

        if (typeof this.desktopMedia.removeEventListener === 'function') {
          this.desktopMedia.removeEventListener('change', this.handleModeChange);
        } else {
          this.desktopMedia.removeListener(this.handleModeChange);
        }

        this.track.removeEventListener('scroll', this.handleScroll);
        this.track.removeEventListener('keydown', this.handleKeydown);
        this.track.removeEventListener('pointerdown', this.handlePointerDown);
        this.track.removeEventListener('pointermove', this.handlePointerMove);
        this.track.removeEventListener('pointerup', this.handlePointerEnd);
        this.track.removeEventListener('pointercancel', this.handlePointerEnd);
        this.track.removeEventListener('dragstart', this.handleDragStart);
        this.track.removeEventListener('click', this.handleTrackClick, true);
        this.removeEventListener('pointerdown', this.handleQuickViewRequest, true);
        this.removeEventListener('click', this.handleQuickViewRequest, true);

        if (this.previousButton) {
          this.previousButton.removeEventListener('click', this.handlePrevious);
        }
        if (this.nextButton) {
          this.nextButton.removeEventListener('click', this.handleNext);
        }

        if (this.scrollFrame) {
          window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = null;
        }
      }

      isSnapActive() {
        return this.desktopMedia.matches ? this.snapDesktop : this.snapMobile;
      }

      observeVisibility() {
        if (!('IntersectionObserver' in window)) {
          this.hasEnteredViewport = true;
          this.observeSlides();
          return;
        }

        this.sectionObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            this.hasEnteredViewport = true;
            this.sectionObserver.disconnect();
            this.sectionObserver = null;
            this.observeSlides();
          },
          {
            rootMargin: '500px 0px',
          }
        );

        this.sectionObserver.observe(this);
      }

      observeSlides() {
        this.disconnectSlideObserver();

        if (!this.slides.length) return;

        if (!('IntersectionObserver' in window)) {
          this.slides.forEach((slide) => this.hydrateMedia(slide));
          return;
        }

        const snapActive = this.isSnapActive();
        this.slideObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;

              this.hydrateMedia(entry.target);
              observer.unobserve(entry.target);
            });
          },
          {
            root: snapActive ? this.track : null,
            rootMargin: snapActive ? '0px 35% 0px 35%' : '500px 0px',
            threshold: 0.01,
          }
        );

        this.slides.forEach((slide) => {
          if (slide.querySelector('[data-fcs-src], [data-fcs-srcset], [data-fcs-poster]')) {
            this.slideObserver.observe(slide);
          }
        });
      }

      disconnectSlideObserver() {
        if (!this.slideObserver) return;

        this.slideObserver.disconnect();
        this.slideObserver = null;
      }

      handleModeChange() {
        this.track.scrollLeft = 0;
        this.updateViewportBleed();
        this.updateControls();
        this.calcNavButtonsPosition();

        if (this.hasEnteredViewport) {
          this.observeSlides();
        }
      }

      handleScroll() {
        if (this.scrollFrame) return;

        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateControls();
        });
      }

      updateControls() {
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const currentScroll = Math.max(0, this.track.scrollLeft);
        const atStart = currentScroll <= 2;
        const atEnd = currentScroll >= maxScroll - 2;

        this.setButtonState(this.previousButton, atStart || !this.isSnapActive());
        this.setButtonState(this.nextButton, atEnd || !this.isSnapActive());

        if (this.pagination) {
          const visibleSlides = this.getVisibleSlideCount();
          const totalSteps = Math.max(1, this.slides.length - visibleSlides + 1);
          const currentStep = Math.min(totalSteps, this.getNearestSlideIndex() + 1);
          const progress = currentStep / totalSteps;
          this.pagination.style.setProperty('--fc-snap-progress', progress.toFixed(4));
        }
      }

      getVisibleSlideCount() {
        if (this.slides.length < 2) return Math.max(1, this.slides.length);

        const firstSlideLeft = this.getSlideLeft(this.slides[0]);
        const secondSlideLeft = this.getSlideLeft(this.slides[1]);
        const slideStep = Math.abs(secondSlideLeft - firstSlideLeft);

        if (slideStep <= 0) return 1;

        return Math.max(
          1,
          Math.min(this.slides.length, Math.round(this.getTrackContentWidth() / slideStep))
        );
      }

      setButtonState(button, disabled) {
        if (!button) return;

        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      }

      handlePrevious(event) {
        event.preventDefault();
        this.scrollBySlide(-1);
      }

      handleNext(event) {
        event.preventDefault();
        this.scrollBySlide(1);
      }

      handleKeydown(event) {
        if (!this.isSnapActive()) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.scrollBySlide(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.scrollBySlide(1);
        }
      }

      scrollBySlide(direction) {
        if (!this.isSnapActive() || !this.slides.length) return;

        const currentIndex = this.getNearestSlideIndex();
        const targetIndex = Math.max(0, Math.min(this.slides.length - 1, currentIndex + direction));
        const targetSlide = this.slides[targetIndex];
        const preloadSlide = this.slides[targetIndex + direction];

        this.hydrateMedia(targetSlide);
        if (preloadSlide) this.hydrateMedia(preloadSlide);

        this.track.scrollTo({
          left: this.getAlignedSlideLeft(targetSlide),
          behavior: 'smooth',
        });
      }

      getNearestSlideIndex() {
        const currentScroll = this.track.scrollLeft;
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const distance = Math.abs(this.getAlignedSlideLeft(slide) - currentScroll);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        return nearestIndex;
      }

      getSlideLeft(slide) {
        const trackRect = this.track.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        return this.track.scrollLeft + slideRect.left - trackRect.left;
      }

      getAlignedSlideLeft(slide) {
        return Math.max(0, this.getSlideLeft(slide) - this.getTrackBleedStart());
      }

      updateViewportBleed() {
        let bleedStart = 0;
        let bleedEnd = 0;

        if (this.contentOverflow && this.snapDesktop && this.desktopMedia.matches) {
          const componentRect = this.getBoundingClientRect();
          const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
          bleedStart = Math.max(0, componentRect.left);
          bleedEnd = Math.max(0, viewportWidth - componentRect.right);
        }

        this.viewportBleedStart = bleedStart;
        this.viewportBleedEnd = bleedEnd;
        this.style.setProperty('--fc-snap-bleed-start', `${bleedStart}px`);
        this.style.setProperty('--fc-snap-bleed-end', `${bleedEnd}px`);
      }

      getTrackBleedStart() {
        return this.viewportBleedStart || 0;
      }

      getTrackBleedEnd() {
        return this.viewportBleedEnd || 0;
      }

      getTrackContentWidth() {
        const computedStyle = window.getComputedStyle(this.track);
        const paddingStart = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingEnd = parseFloat(computedStyle.paddingRight) || 0;
        return Math.max(
          0,
          this.track.clientWidth - paddingStart - paddingEnd
        );
      }

      handlePointerDown(event) {
        if (!this.isSnapActive() || event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('button, input, select, textarea, summary')) return;

        this.dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScroll: this.track.scrollLeft,
          moved: false,
        };
      }

      handlePointerMove(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        const distance = event.clientX - this.dragState.startX;

        // 移动距离不足时仍按普通点击处理
        if (!this.dragState.moved && Math.abs(distance) < 5) return;

        // 确认开始拖拽后才捕获指针
        if (!this.dragState.moved) {
          this.dragState.moved = true;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(event.pointerId);
        }

        this.track.scrollLeft = this.dragState.startScroll - distance;
        event.preventDefault();
      }

      handlePointerEnd(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        const didMove = this.dragState.moved;
        this.dragState = null;
        this.track.classList.remove('is-dragging');

        if (this.track.hasPointerCapture(event.pointerId)) {
          this.track.releasePointerCapture(event.pointerId);
        }

        if (didMove) {
          // 真正发生拖拽时，阻止拖拽结束后的误点击
          this.suppressClick = true;

          window.setTimeout(() => {
            this.suppressClick = false;
          }, 0);

          window.requestAnimationFrame(() => {
            const nearestSlide = this.slides[this.getNearestSlideIndex()];
            if (!nearestSlide) return;

            this.track.scrollTo({
              left: this.getAlignedSlideLeft(nearestSlide),
              behavior: 'smooth',
            });
          });
        }
      }

      handleDragStart(event) {
        if (this.isSnapActive()) {
          event.preventDefault();
        }
      }

      handleTrackClick(event) {
        if (!this.suppressClick) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }

      hydrateMedia(root, includeQuickView = false) {
        if (!root) return;

        const videosToReload = new Set();
        const shouldRestore = (media) =>
          includeQuickView || !media.closest('quick-view-modal');

        root.querySelectorAll('[data-fcs-poster]').forEach((media) => {
          if (!shouldRestore(media)) return;

          this.restoreAttribute(media, 'poster');
          if (media.matches('video')) videosToReload.add(media);
        });

        root.querySelectorAll('[data-fcs-srcset]').forEach((media) => {
          if (!shouldRestore(media)) return;

          this.prepareImage(media);
          this.restoreAttribute(media, 'srcset');

          const video = media.closest('video');
          if (video) videosToReload.add(video);
        });

        root.querySelectorAll('[data-fcs-src]').forEach((media) => {
          if (!shouldRestore(media)) return;

          this.prepareImage(media);
          this.restoreAttribute(media, 'src');

          const video = media.closest('video');
          if (video) videosToReload.add(video);
        });

        videosToReload.forEach((video) => video.load());
      }

      restoreAttribute(media, attributeName) {
        const deferredAttribute = `data-fcs-${attributeName}`;
        const value = media.getAttribute(deferredAttribute);
        if (value === null) return;

        media.setAttribute(attributeName, value);
        media.removeAttribute(deferredAttribute);
      }

      prepareImage(media) {
        if (!media.matches('img')) return;

        media.loading = 'lazy';
        media.setAttribute('fetchpriority', 'low');
        media.addEventListener('load', this.handleMediaLoad, { once: true });
      }

      handleMediaLoad() {
        this.calcNavButtonsPosition();
      }

      handleQuickViewRequest(event) {
        const trigger = event.target.closest && event.target.closest('[aria-controls]');
        if (!trigger || !this.contains(trigger)) return;

        const modalId = trigger.getAttribute('aria-controls');
        if (!modalId) return;

        const modal = document.getElementById(modalId);
        if (modal && modal.matches('quick-view-modal')) {
          this.hydrateMedia(modal, true);
        }
      }

      calcNavButtonsPosition() {
        if (this.dataset.navigationPosition !== 'center_image') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        const controls = this.block.querySelector('.swiper-controls');
        if (controls && firstMedia && firstMedia.clientHeight > 0) {
          controls.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight, 10) / 2 + 'px'
          );
        }
      }
    }
  );
}